// src/features/groups/components/GroupCreate.tsx

import {
  Create,
  SimpleForm,
  TextInput,
  required,
  minLength,
  maxLength,
  useTranslate,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { GroupWork as GroupIcon } from '@mui/icons-material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { RegistryLinkInput } from '@/components/forms/RegistryLinkInput';
import { MyFormLayout, FormCard, MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';

/**
 * Validation rules for group creation
 */
const validateGroupName = [
  required('resources.groups.validation.name_required'),
  minLength(3, 'resources.groups.validation.name_min_length'),
  maxLength(100, 'resources.groups.validation.name_max_length'),
];

const validateDescription = [
  maxLength(500, 'resources.groups.validation.description_max_length'),
];

/**
 * Group creation component
 * Only SuperAdmin can access this
 */
export const GroupCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const handleCancel = () => navigate('/admin-options?tab=groups');

  const onSuccess = () => {
    notify('resources.groups.messages.create_success', { type: 'success' });
    redirect('/admin-options?tab=groups');
  };

  const onError = (error: any) => {
    const errorMessage = error?.body?.error?.message || 
                        error?.message || 
                        translate('resources.groups.messages.create_error');
    notify(errorMessage, { type: 'error' });
  };

  return (
    <Create
      mutationOptions={{ onSuccess, onError }}
      redirect="list"
      title={translate('resources.groups.create.title')}
    >
      <SimpleForm toolbar={<MyToolbar resource="groups" onCancel={handleCancel} />}>
        <MyFormContainer maxWidth="md">
          <MyFormLayout centered maxColumns={1}>
            <FormCard xs={12}>
              <MyFormSection
                title={translate('resources.groups.create.title')}
                subtitle={translate('resources.groups.create.subtitle')}
                icon={<GroupIcon />}
                variant="clean"
              >
                {/* Group Name */}
                <TextInput
                  source="name"
                  label={translate('resources.groups.fields.name')}
                  validate={validateGroupName}
                  fullWidth
                  helperText={translate('resources.groups.validation.name_min_length')}
                />

                {/* Description */}
                <TextInput
                  source="description"
                  label={translate('resources.groups.fields.description')}
                  validate={validateDescription}
                  fullWidth
                  multiline
                  rows={4}
                  helperText={translate('resources.groups.validation.description_max_length')}
                />

                {/* Record of the shared registry */}
                <RegistryLinkInput
                  source="directoryId"
                  kind="organization"
                  label={translate('resources.groups.fields.directoryId')}
                />
              </MyFormSection>
            </FormCard>
          </MyFormLayout>
        </MyFormContainer>
      </SimpleForm>
    </Create>
  );
};